//
// Copyright © 2024 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { AnyComponent, IntlString } from '@hcengineering/ui'
import type { Asset, Plugin } from '@hcengineering/platform'
import { plugin } from '@hcengineering/platform'
import type { Mixin, Ref, Timestamp } from '@hcengineering/core'
import type { Employee } from '@hcengineering/contact'

/**
 * @public
 */
export const financialManagerId = 'financial-manager' as Plugin

/**
 * Тип оплаты сотрудника.
 * @public
 */
export enum PaymentType {
  HOURLY = 'hourly',
  FIXED = 'fixed'
}

/**
 * Финансовые параметры сотрудника.
 * @public
 */
export interface EmployeeFinancials extends Mixin<Employee> {
  paymentType: PaymentType
  /** Часовая ставка (для HOURLY) */
  hourlyRate?: number
  /** Месячная зарплата (для FIXED) */
  monthlySalary?: number
  /** Валюта: 'USD', 'EUR', 'UAH', ... */
  currency: string
  /** Банковские реквизиты / IBAN / кошелёк */
  bankDetails?: string
  /** Дата следующей выплаты */
  nextPayDate?: Timestamp
  /** Внутренние заметки по сотруднику */
  notes?: string
}

export default plugin(financialManagerId, {
  mixin: {
    EmployeeFinancials: '' as Ref<Mixin<EmployeeFinancials>>
  },
  component: {
    EmployeeProfileFooter: '' as AnyComponent,
    FinancialsSection: '' as AnyComponent,
    EmployeeTimeStats: '' as AnyComponent,
    EmployeeProjectsSection: '' as AnyComponent,
    EmployeeDocuments: '' as AnyComponent
  },
  string: {
    FinancialManager: '' as IntlString,
    PaymentType: '' as IntlString,
    Hourly: '' as IntlString,
    Fixed: '' as IntlString,
    HourlyRate: '' as IntlString,
    MonthlySalary: '' as IntlString,
    Currency: '' as IntlString,
    BankDetails: '' as IntlString,
    NextPayDate: '' as IntlString,
    SetupFinancials: '' as IntlString,
    FinancialParameters: '' as IntlString,
    WorkedTime: '' as IntlString,
    Projects: '' as IntlString,
    Documents: '' as IntlString,
    Role: '' as IntlString,
    Notes: '' as IntlString,
    GeneralInfo: '' as IntlString,
    Save: '' as IntlString,
    UnknownProject: '' as IntlString,
    NotesPlaceholder: '' as IntlString,
    ThisMonth: '' as IntlString,
    LastMonth: '' as IntlString,
    AllTime: '' as IntlString,
    Earnings: '' as IntlString
  },
  icon: {
    FinancialManager: '' as Asset
  }
})
